import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import DocumentViewer from "../components/DocumentViewer/DocumentViewer";
import Leftbar from "../components/Leftbar/Leftbar";
import SearchResultList from "../components/SearchResultList/SearchResultList";
import SearchBar from "../components/SearchBar/SearchBar";
import TextCTA from "../components/TextCTA/TextCTA";
import "../pagesStyling/SearchPage.css";
import { AuthContext } from "../components/AuthContext/AuthContext";
import { API_BASE_URL } from "../apiConfig";

interface SourceOrigin {
  id: string;
  name: string;
  icon: string;
}

interface SourceType {
  id: string;
  name: string;
  icon: string;
}

interface ApiResponse {
  source_types: SourceType[];
  source_origins: SourceOrigin[];
}

interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

interface SearchApiResult {
  title: string;
  excerpt: string;
  avg_score: number;
}

interface SearchResult {
  documentName: string;
  fileType: string;
  author: string;
  lastModified: string;
  highlights: { page: number; text: string }[];
}

function Search() {
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [isSearched, setIsSearched] = useState(false);
  const [sourceOptions, setSourceOptions] = useState<Option[]>([]);
  const [typeOptions, setTypeOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const { token } = useContext(AuthContext) || {};

  useEffect(() => {
    const fetchSources = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/sources`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        });

        const data = response.data as ApiResponse;

        // Map and filter source origins
        const formattedSourceOptions: Option[] = data.source_origins.map(
          (source: SourceOrigin) => ({
            label: source.name,
            value: source.id,
            disabled: ["confluence", "jira", "windchill"].includes(source.id),
          })
        );

        // Map type options
        const formattedTypeOptions: Option[] = data.source_types.map(
          (type: SourceType) => ({
            label: type.name,
            value: type.id,
          })
        );

        // Set default selection for Google Drive and all type options
        const googleDriveId = "gdrive";
        const defaultSources: string[] = [];

        // Add Google Drive if available and not disabled
        if (
          formattedSourceOptions.some(
            (option) => option.value === googleDriveId && !option.disabled
          )
        ) {
          defaultSources.push(googleDriveId);
        }

        const allTypeIds = formattedTypeOptions.map((option) => option.value);
        defaultSources.push(...allTypeIds);

        setSelectedSources(defaultSources);
        setSourceOptions(formattedSourceOptions);
        setTypeOptions(formattedTypeOptions);
      } catch (error) {
        console.error("Error fetching sources:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSources();
  }, [token]);

  const handleSearch = async (query: string) => {
    if (!query && isSearched) {
      setSearchQuery("");
      setSearchResults([]);
      setIsSearched(false);
      return;
    }

    if (!query || !token) return;

    setSearchQuery(query);
    setIsSearched(true);

    try {
      const queryParams = new URLSearchParams();
      queryParams.append("prompt", query);
      const sourcesForApi = selectedSources.filter(
        (source) => source !== "gdrive"
      );
      sourcesForApi.forEach((source) => queryParams.append("sources", source));

      const response = await axios.get(
        `${API_BASE_URL}/search/?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      const data = response.data;
      const results: SearchApiResult[] = data.document_results;

      const transformedResults: SearchResult[] = results.map((result) => ({
        documentName: result.title,
        fileType: result.title.split(".").pop() || "unknown",
        author: "Anja.Aust@Roche.com",
        lastModified: "12/11/2024 18:43",
        highlights: [
          {
            page: 1,
            text:
              result.excerpt.length > 100
                ? result.excerpt.substring(0, 200) + "..."
                : result.excerpt,
          },
        ],
      }));

      setSearchResults(transformedResults);
    } catch (error) {
      console.error("Error performing search:", error);
      setSearchResults([]);
    }
  };

  const handleSelectionChange = (selected: string[]) => {
    setSelectedSources(selected);
  };

  if (isLoading) {
    return <div>Loading sources...</div>;
  }

  return (
    <div className="page-container search-page">
      <Leftbar
        title="Search"
        hasMultipleCheckbox={true}
        sourceOptions={sourceOptions}
        typeOptions={typeOptions}
        titleFirstCheckbox="Sources"
        titleSecondCheckbox="Types"
        selectedValues={selectedSources}
        onSelectionChange={handleSelectionChange}
      />
      <div className="search-page-container">
        <div
          className={`search-page-upper ${
            !isSearched ? "initial" : "searched"
          }`}
        >
          <p>
            Search your documents to find the information you are looking for.
          </p>

          <SearchBar
            placeholder="Search documents..."
            onSearch={handleSearch}
          />

          {isSearched && <div>Showing results for "{searchQuery}"</div>}
        </div>

        <div className={`search-page-content ${!isSearched ? "hidden" : ""}`}>
          {isSearched ? (
            <>
              <SearchResultList results={searchResults} />
            </>
          ) : (
            <p className="no-results">Enter a search term to display results</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Search;
